import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { IconPhoneCheck } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import DayField from "src/components/shared/DayField";
import { FormField } from "src/pages/catalogue/FormBits";
import { ENTRIES, REFUSAL_GROUNDS, dayOf, decisionsKnown, groundKeys, groundTitle, grantedOf, grantedWords, outcomeOfStatus } from "./decisions";
import { dateTime, statusLabel } from "./format";
import { toneColor } from "./workspace";

// The decision record (API docs/visa-decisions.md, migration 023): what the
// consulate decided, entered from the letter. For a refusal the grounds
// ticked on the form; for a visa what was granted. The customer's page
// explains a refusal from it; the team's stats read it.

const ORDER = ["grounds", "entries", "valid_from", "valid_until", "days", "decided_on", "notified_on", "note"];

// A message that appears under a long form scrolls into view, so it isn't
// hidden below the fold while the button above it changes.
export function useShowWhenSet(value) {
  const ref = useRef(null);
  useEffect(() => {
    if (value) ref.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [value]);
  return ref;
}

// After a failed save, the first field with an error takes the focus.
export function focusFirstError(errors, refs) {
  const name = ORDER.find((key) => errors[key] && refs.current[key]);
  const element = name && refs.current[name];
  if (!element) return;
  element.scrollIntoView?.({ block: "center" });
  element.focus?.();
}

// The fields, in two columns from tablet up: what the letter says on the
// left (grounds, or what was granted), when and where on the right.
// grounds: the refusal grounds this kind of visa has (decisions.groundsFor):
// the Schengen form's for a Schengen short stay, a short list otherwise.
// filled: where prefilled values came from (round 2, nadia-22): { consulate:
// "Filled in from case #1201, …", days: "Filled in from the product …" },
// shown while the field still holds that value.
export function DecisionFields({ form, onChange, errors, refs, idBase, consulateRemembered, filled = {}, grounds = REFUSAL_GROUNDS, schengen = true }) {
  const set = (patch) => onChange({ ...form, ...patch });
  const ref = (name) => (element) => {
    refs.current[name] = element;
  };
  const refused = form.outcome === "refused";
  const other = refused && form.grounds.includes("other");
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.1fr) minmax(0, 1fr)" }, columnGap: 4, rowGap: 2.5 }}>
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        {refused ? (
          <Box component="fieldset" aria-describedby={errors.grounds ? `${idBase}-grounds-error` : `${idBase}-grounds-hint`} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
            <Typography component="legend" variant="subtitle2" sx={{ fontWeight: 600, p: 0 }}>
              Grounds on the letter
            </Typography>
            <Typography id={`${idBase}-grounds-hint`} variant="body2" color="text.secondary" mb={0.5}>
              {schengen
                ? "Tick each box ticked on the refusal form."
                : "Tick each reason the letter gives. For any other reason, tick “Another reason” and write it in the note."}
            </Typography>
            <FormGroup>
              {grounds.map((ground, index) => (
                <FormControlLabel
                  key={ground.key}
                  label={ground.title}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.grounds.includes(ground.key)}
                      inputRef={index === 0 ? ref("grounds") : undefined}
                      onChange={(e) =>
                        set({ grounds: e.target.checked ? [...form.grounds, ground.key] : form.grounds.filter((k) => k !== ground.key) })
                      }
                      sx={{ py: 0.5 }}
                    />
                  }
                  sx={{ ml: -0.75, mr: 0, alignItems: "flex-start", "& .MuiFormControlLabel-label": { fontSize: "0.875rem", pt: "7px", pl: 0.5, lineHeight: 1.35 } }}
                />
              ))}
            </FormGroup>
            {errors.grounds ? (
              <Typography id={`${idBase}-grounds-error`} role="alert" variant="body2" color="error.main" mt={0.5}>
                {errors.grounds}
              </Typography>
            ) : null}
          </Box>
        ) : (
          <>
            <FormField id={`${idBase}-entries`} label="Entries" optional>
              <ToggleButtonGroup
                id={`${idBase}-entries`}
                size="small"
                exclusive
                value={form.entries || null}
                onChange={(_, next) => set({ entries: next || "" })}
                aria-labelledby={`${idBase}-entries-label`}
                sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 1.75, minHeight: 40 } }}
              >
                {ENTRIES.map((entry) => (
                  <ToggleButton key={entry.value} value={entry.value}>
                    {entry.label.replace(" entry", "")}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </FormField>
            <DayField label="Valid from" optional value={form.valid_from} onChange={(v) => set({ valid_from: v })} error={errors.valid_from} hint="As printed on the visa." inputRef={ref("valid_from")} />
            <DayField label="Valid until" optional showToday={false} value={form.valid_until} onChange={(v) => set({ valid_until: v })} error={errors.valid_until} inputRef={ref("valid_until")} />
            <FormField id={`${idBase}-days`} label="Days allowed" optional hint={filled.days || "The duration of stay on the visa, like 90."} error={errors.days}>
              <TextField
                id={`${idBase}-days`}
                value={form.days}
                onChange={(e) => set({ days: e.target.value })}
                error={!!errors.days}
                inputRef={ref("days")}
                inputProps={{ inputMode: "numeric", maxLength: 4, "aria-describedby": errors.days ? `${idBase}-days-error` : `${idBase}-days-hint` }}
                sx={{ width: 120 }}
              />
            </FormField>
          </>
        )}
      </Stack>
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        <FormField
          id={`${idBase}-consulate`}
          label="Consulate"
          optional
          hint={
            consulateRemembered
              ? "The last one used for this visa. Change it if this one says another."
              : filled.consulate || "Who decided, like “French consulate general, Beirut”."
          }
        >
          <TextField
            id={`${idBase}-consulate`}
            fullWidth
            value={form.consulate}
            onChange={(e) => set({ consulate: e.target.value })}
            inputProps={{ maxLength: 160, "aria-describedby": `${idBase}-consulate-hint` }}
          />
        </FormField>
        <DayField
          label="Decided on"
          optional
          value={form.decided_on}
          onChange={(v) => set({ decided_on: v })}
          error={errors.decided_on}
          hint={refused ? "The date on the letter." : "The visa’s date of issue."}
          inputRef={ref("decided_on")}
        />
        <DayField label="Customer told on" value={form.notified_on} onChange={(v) => set({ notified_on: v })} error={errors.notified_on} hint="Today, unless they heard earlier." inputRef={ref("notified_on")} />
        <FormField
          id={`${idBase}-note`}
          label="Note for the team"
          optional={!other}
          hint={other ? "Write the other reason from the letter. Only the team sees this." : "Only the team sees this."}
          error={errors.note}
        >
          <TextField
            id={`${idBase}-note`}
            fullWidth
            multiline
            minRows={2}
            value={form.note}
            onChange={(e) => set({ note: e.target.value })}
            error={!!errors.note}
            inputRef={ref("note")}
            inputProps={{ maxLength: 2000, "aria-describedby": errors.note ? `${idBase}-note-error` : `${idBase}-note-hint` }}
          />
        </FormField>
      </Stack>
    </Box>
  );
}

DecisionFields.propTypes = {
  form: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  refs: PropTypes.object.isRequired,
  idBase: PropTypes.string.isRequired,
  consulateRemembered: PropTypes.bool,
  filled: PropTypes.object,
  grounds: PropTypes.array,
  schengen: PropTypes.bool,
};

const calledBy = (decision) => decision.called_by_name || (typeof decision.called_by === "string" ? decision.called_by : "") || "the team";

// The record on the case, with Edit. Shown on an approved or refused case
// (or wherever a record exists); an older API without decisions shows none.
export function DecisionCard({ caseData, canEdit, unavailable, onRecord, onEdit }) {
  if (!decisionsKnown(caseData)) return null;
  const decision = caseData.decision;
  const pending = outcomeOfStatus(caseData.status);
  if (!decision && !pending) return null;
  if (!decision) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" component="h2" mb={0.5}>
            Decision
          </Typography>
          {unavailable ? (
            <Typography variant="body2" color="text.secondary">
              Needs migration 023: decisions can’t be recorded on this server yet.
            </Typography>
          ) : (
            <Stack spacing={1} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary">
                {pending === "refused" ? "No record of the refusal yet: its grounds explain it to the customer." : "No record of what was granted yet."}
              </Typography>
              {canEdit ? (
                <ActionButton variant={pending === "refused" ? "contained" : "outlined"} onClick={() => onRecord(pending)}>
                  Record the decision
                </ActionButton>
              ) : null}
            </Stack>
          )}
        </CardContent>
      </Card>
    );
  }

  const refused = decision.outcome !== "approved";
  // A status moved back keeps the record but hides it from the customer;
  // it can be edited again once the status says the same.
  const matches = pending === (refused ? "refused" : "approved");
  const grounds = groundKeys(decision.grounds);
  const granted = grantedOf(decision);
  const dates = [
    dayOf(decision.decided_on) && `Decided ${dayOf(decision.decided_on)}`,
    dayOf(decision.notified_on) && `customer told ${dayOf(decision.notified_on)}`,
    Number(decision.days_taken) > 0 && `took ${decision.days_taken} ${Number(decision.days_taken) === 1 ? "day" : "days"}`,
  ].filter(Boolean);
  return (
    <Card variant="outlined" id="case-decision">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" component="h2">
            Decision
          </Typography>
          {canEdit && matches ? (
            <ActionButton variant="text" size="small" onClick={() => onEdit(refused ? "refused" : "approved")} aria-label="Edit the decision">
              Edit
            </ActionButton>
          ) : null}
        </Stack>
        <Stack direction="row" columnGap={1} rowGap={0.5} alignItems="center" flexWrap="wrap">
          <Chip size="small" color={refused ? "error" : "success"} label={refused ? "Refused" : "Approved"} />
          {dates.length ? (
            <Typography variant="body2" color="text.secondary">
              {dates.join(" · ").replace(/^./, (c) => c.toUpperCase())}
            </Typography>
          ) : null}
        </Stack>
        {!matches ? (
          <Typography variant="body2" mt={1} sx={{ color: toneColor("warn"), fontWeight: 600 }}>
            The status is now {statusLabel(caseData.status)}: the customer doesn’t see this decision.
          </Typography>
        ) : null}
        {decision.consulate ? (
          <Typography variant="body2" mt={1}>
            {decision.consulate}
          </Typography>
        ) : null}
        {refused ? (
          grounds.length ? (
            <Box component="ul" aria-label="Grounds on the letter" sx={{ m: 0, mt: 1, pl: 2.5 }}>
              {grounds.map((key) => (
                <Typography component="li" variant="body2" key={key}>
                  {groundTitle(key)}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" mt={1}>
              No grounds recorded.
            </Typography>
          )
        ) : grantedWords(granted) ? (
          <Typography variant="body2" mt={1}>
            {grantedWords(granted)}
          </Typography>
        ) : null}
        {decision.note ? (
          <Typography variant="body2" color="text.secondary" mt={1} sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            Team note: {decision.note}
          </Typography>
        ) : null}
        {refused && decision.appeal?.deadline ? (
          <Typography variant="body2" mt={1}>
            Appeal until <b>{dayOf(decision.appeal.deadline)}</b>
          </Typography>
        ) : null}
        {refused && matches ? (
          decision.called_at ? (
            <Typography variant="body2" mt={1} sx={{ display: "flex", alignItems: "center", gap: 0.75, color: toneColor("pass"), fontWeight: 600 }}>
              <IconPhoneCheck size={16} aria-hidden />
              Called by {calledBy(decision)} on {dateTime(decision.called_at)}
            </Typography>
          ) : (
            <Typography variant="body2" mt={1} sx={{ color: toneColor("warn"), fontWeight: 600 }}>
              Not called yet
            </Typography>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

DecisionCard.propTypes = {
  caseData: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  unavailable: PropTypes.bool,
  onRecord: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};
